/**
 * Upload types: JPEG, JPG, PNG, GIF, MP4, MOV, QT only.
 */

const IMAGE_EXT = [".jpeg", ".jpg", ".png", ".gif"];
const VIDEO_EXT = [".mp4", ".mov", ".qt"];
const IMAGE_MIMES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
const VIDEO_MIMES = ["video/mp4", "video/quicktime", "video/mov", "video/x-quicktime"];

// Extension-only so the system file picker shows only these (e.g. hides .jfif)
export const ACCEPT_IMAGES_AND_VIDEOS = ".jpeg,.jpg,.png,.gif,.mp4,.mov,.qt";
export const ACCEPT_IMAGES_ONLY = ".jpeg,.jpg,.png,.gif";
export const ACCEPT_COVER_IMAGES = ".jpeg,.jpg,.png";
export const ACCEPT_VIDEOS_ONLY = ".mp4,.mov,.qt";
export const ACCEPT_GIFS_ONLY = ".gif";

export const ALLOWED_IMAGE_EXTENSIONS = IMAGE_EXT;
export const ALLOWED_VIDEO_EXTENSIONS = VIDEO_EXT;
export const ALLOWED_IMAGE_TYPES = IMAGE_MIMES;
export const ALLOWED_VIDEO_TYPES = VIDEO_MIMES;

const ALLOWED_EXT = [...IMAGE_EXT, ...VIDEO_EXT];
export const MAX_UPLOAD_MB = 100;
/** AI Community Inbox reply attachment limit (paperclip in Quick Reply). */
export const MAX_INBOX_REPLY_UPLOAD_MB = 30;
const MAX_MB = MAX_UPLOAD_MB;
export const MAX_MEDIA_LIBRARY_SINGLE_MB = MAX_UPLOAD_MB;
export const MAX_MEDIA_LIBRARY_BULK_MB = 500;
/** @deprecated Use MAX_MEDIA_LIBRARY_BULK_MB */
export const MAX_MEDIA_LIBRARY_MB = MAX_MEDIA_LIBRARY_BULK_MB;

export const MAX_UPLOAD_FILE_SIZE_LABEL = `Max ${MAX_UPLOAD_MB}MB`;

export const MEDIA_LIBRARY_UPLOAD_COPY = {
  perFile: `Up to ${MAX_UPLOAD_MB}MB per file`,
  bulk: `Up to ${MAX_MEDIA_LIBRARY_BULK_MB}MB per upload`,
  limitsLabel: `${MAX_UPLOAD_MB}MB per file · ${MAX_MEDIA_LIBRARY_BULK_MB}MB bulk`,
};

/** Returns error message or null. Use extension first so .jfif (image/jpeg) is rejected. */
export function validateUploadFile(file, maxMb = MAX_MB) {
  const ext = (file.name || "").toLowerCase().slice((file.name || "").lastIndexOf("."));
  if (!ALLOWED_EXT.includes(ext)) return `Unsupported file "${file.name}". Allowed: JPEG, JPG, PNG, GIF, MP4, MOV, QT only.`;
  const isImage = IMAGE_MIMES.includes(file.type) || IMAGE_EXT.includes(ext);
  const isVideo = VIDEO_MIMES.includes(file.type) || VIDEO_EXT.includes(ext);
  if (!isImage && !isVideo) return `Unsupported file "${file.name}". Allowed: JPEG, JPG, PNG, GIF, MP4, MOV, QT only.`;
  const maxBytes = maxMb * 1024 * 1024;
  if (file.size > maxBytes) return `${isImage ? "Image" : "Video"} "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Max ${maxMb}MB per file.`;
  return null;
}

/** Media Library: 100MB per file, 500MB total per upload batch. */
export function validateMediaLibraryUploadFiles(files) {
  const validFiles = [];
  const invalidFiles = [];
  let totalBytes = 0;
  const bulkMaxBytes = MAX_MEDIA_LIBRARY_BULK_MB * 1024 * 1024;

  for (const file of files) {
    const err = validateUploadFile(file, MAX_MEDIA_LIBRARY_SINGLE_MB);
    if (err) {
      invalidFiles.push(err);
      continue;
    }
    if (totalBytes + file.size > bulkMaxBytes) {
      invalidFiles.push(
        `"${file.name}" cannot be added — total upload would exceed ${MAX_MEDIA_LIBRARY_BULK_MB}MB.`,
      );
      continue;
    }
    totalBytes += file.size;
    validFiles.push(file);
  }

  return { validFiles, invalidFiles };
}

export function isVideoFile(file) {
  if (!file) return false;
  const ext = (file.name || "").toLowerCase().slice((file.name || "").lastIndexOf("."));
  return VIDEO_MIMES.includes((file.type || "").toLowerCase()) || VIDEO_EXT.includes(ext);
}

// Shared UI copy for upload (Setting up + AI Asset Manager – same design)
export const UPLOAD_COPY = {
  intro: "Upload your media and let our AI automatically categorize, optimize, and organize your assets for maximum impact across all your campaigns.",
  introLine1: "Upload your media and let our AI automatically categorize, optimize, and",
  introLine2: "organize your assets for maximum impact across all your posts.",
  limits: "You can upload up to 10 images and 2 videos.",
  cta: "Drop files here or click to browse",
  size: `Upload file up to ${MAX_UPLOAD_MB}MB each`,
  supported: "Supports JPEG, JPG, PNG, GIF, MP4, MOV, QT",
  maxImagesError: (current) => `Maximum 10 images allowed. Currently you have ${current}.`,
  maxVideosError: (current) => `Maximum 2 videos allowed. You currently have ${current}.`,
  maxImagesLimit: "Maximum 10 images can be uploaded.",
  maxVideosLimit: "Maximum 2 videos can be uploaded.",
  // someSkipped: "Some files were skipped:",
};
